import React, { useEffect, useState, useCallback } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import IconButton from "@mui/material/IconButton";
import VisibilityIcon from "@mui/icons-material/Visibility";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import ClearIcon from "@mui/icons-material/Clear";
import PhoneIcon from "@mui/icons-material/Phone";
import EmailIcon from "@mui/icons-material/Email";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import WorkIcon from "@mui/icons-material/Work";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import BusinessIcon from "@mui/icons-material/Business";
import PersonIcon from "@mui/icons-material/Person";
import CloseIcon from "@mui/icons-material/Close";
import Pagination from "@mui/material/Pagination";
import Select from "@mui/material/Select";
import MenuItem from "@mui/material/MenuItem";
import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import Button from "@mui/material/Button";
import TextField from "@mui/material/TextField";
import FormControl from "@mui/material/FormControl";
import ConfirmDialog from "components/Dialogs/ConfirmDialog";
import EditDialog from "components/Dialogs/EditDialog";

// Material Dashboard 2 React components
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDAvatar from "components/MDAvatar";

// Material Dashboard 2 React example components
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";
import DataTable from "examples/Tables/DataTable";

// Data
import { getJobs } from "data/controllers/jobsController";
import { changeJobStatus } from "data/controllers/jobsController";
import { deleteJob } from "data/controllers/jobsController";
import { getJobApplications } from "data/controllers/jobsController";
import { updateJob } from "data/controllers/jobsController";

const jobColumns = [
  { Header: "title", accessor: "title", align: "left" },
  { Header: "salary", accessor: "salary", align: "center" },
  { Header: "job type", accessor: "jobType", align: "center" },
  { Header: "resume", accessor: "applications", align: "center" },
  { Header: "created at", accessor: "createdAt", align: "center" },
  { Header: "status", accessor: "status", align: "center" },
  { Header: "action", accessor: "action", align: "center" },
];

function formatJobType(input) {
  if (!input) return input;

  const JOB_TYPE = {
    remote: "Remote",
    fullTime: "Full-Time",
    partTime: "Part-Time",
    contract: "Contract",
    internship: "Internship",
  };

  return JOB_TYPE[input] || input;
}

function Jobs() {
  const location = useLocation();
  const navigate = useNavigate();
  const [jobRows, setJobRows] = useState([]);
  const [jobsData, setJobsData] = useState([]);
  const [jobPage, setJobPage] = useState(1);
  const [jobTotal, setJobTotal] = useState(0);
  const [jobLimit, setJobLimit] = useState(10);
  const [totalPages, setTotalPages] = useState(1);
  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState("");
  const [sortOption, setSortOption] = useState("newToOld");
  const [statusFilter, setStatusFilter] = useState("all");

  const [errorDialogOpen, setErrorDialogOpen] = useState(false);
  const [errorDialogMessage, setErrorDialogMessage] = useState("");

  const [jobToDelete, setJobToDelete] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);

  // Phone call dialog state
  const [phoneDialogOpen, setPhoneDialogOpen] = useState(false);
  const [selectedPhoneNumber, setSelectedPhoneNumber] = useState("");
  const [selectedRecruiterInfo, setSelectedRecruiterInfo] = useState(null);

  // Edit dialog state
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [selectedJob, setSelectedJob] = useState(null);

  // Rejection reason dialog state
  const [rejectionDialogOpen, setRejectionDialogOpen] = useState(false);
  const [rejectionReason, setRejectionReason] = useState("");
  const [jobToReject, setJobToReject] = useState(null);

  // View applicants dialog state
  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  const [viewApplicants, setViewApplicants] = useState([]);

  const sortOptions = [
    { label: "New to Old", value: "newToOld" },
    { label: "Old to New", value: "oldToNew" },
    { label: "Alphabetically", value: "alphabetically" },
  ];

  const handleSearchChange = (event) => {
    setSearchTerm(event.target.value);
  };

  const handleKeyDown = (event) => {
    if (event.key === "Enter") {
      setDebouncedSearchTerm(searchTerm); // Immediate search on Enter
      setJobPage(1);
    }
  };

  const handleClearSearch = () => {
    setSearchTerm("");
    setDebouncedSearchTerm("");
    setJobPage(1);
  };

  const handleSortChange = (event) => {
    setSortOption(event.target.value);
    setJobPage(1);
  };

  const handleStatusFilterChange = (event) => {
    const selectedStatus = event.target.value;
    setStatusFilter(selectedStatus);
    setJobPage(1); // Reset to first page when filter changes

    // Update URL without causing a page reload
    const newSearchParams = new URLSearchParams();
    if (selectedStatus !== "all") {
      newSearchParams.set("status", selectedStatus);
    }
    const newUrl = `/jobs${newSearchParams.toString() ? `?${newSearchParams.toString()}` : ""}`;
    navigate(newUrl, { replace: true });
  };

  const handleStatusChange = useCallback(async (jobId, newStatus) => {
    if (newStatus === "rejected") {
      setJobsData((currentJobs) => {
        const job = currentJobs.find((j) => j._id === jobId);
        setJobToReject({ jobId, job });
        setRejectionDialogOpen(true);
        return currentJobs; // Return unchanged data
      });
      return;
    }

    try {
      await changeJobStatus(jobId, newStatus);
      setJobsData((prevJobs) =>
        prevJobs.map((job) => (job._id === jobId ? { ...job, status: newStatus } : job))
      );
    } catch (error) {
      setErrorDialogMessage(error.message || "Failed to update job status");
      setErrorDialogOpen(true);
    }
  }, []);

  const handleRejectJob = async () => {
    if (!jobToReject) return;

    try {
      await changeJobStatus(jobToReject.jobId, "rejected", rejectionReason);
      setJobsData((prevJobs) =>
        prevJobs.map((job) =>
          job._id === jobToReject.jobId
            ? { ...job, status: "rejected", rejectionError: rejectionReason }
            : job
        )
      );
      setRejectionDialogOpen(false);
      setRejectionReason("");
      setJobToReject(null);
    } catch (error) {
      setErrorDialogMessage(error.message || "Failed to reject job");
      setErrorDialogOpen(true);
    }
  };

  const handleEditJob = useCallback((job) => {
    setSelectedJob(job);
    setEditDialogOpen(true);
  }, []);

  const confirmEditJob = async (event) => {
    event.preventDefault();
    if (!selectedJob) return;

    try {
      if ((selectedJob.salary?.salaryType === "fixed" && (selectedJob.salary.fixed || 0) <= 0)
        || (selectedJob.salary?.salaryType === "range" && ((selectedJob.salary.min || 0) <= 0 || (selectedJob.salary.max || 0) <= 0))) {
        setErrorDialogMessage("Invalid salary");
        setErrorDialogOpen(true);
        return;
      }

      const response = await updateJob(selectedJob._id, selectedJob);
      setJobsData((prevJobs) =>
        prevJobs.map((job) => (job._id === selectedJob._id ? response : job))
      );
      setEditDialogOpen(false);
      setSelectedJob(null);
    } catch (error) {
      setErrorDialogMessage(error.message || "Failed to update job");
      setErrorDialogOpen(true);
    }
  };

  const handleViewClick = useCallback(async (jobId) => {
    try {
      const { applications } = await getJobApplications(jobId);
      console.log('applications data', applications);
      setViewApplicants(applications);
      setViewDialogOpen(true);
    } catch (error) {
      setErrorDialogMessage(error.message || "Failed to load applicants");
      setErrorDialogOpen(true);
    }
  }, []);

  // Phone call handler
  const handlePhoneClick = useCallback((phoneNumber, recruiterInfo) => {
    if (!phoneNumber) {
      setErrorDialogMessage("Phone number not available for this job");
      setErrorDialogOpen(true);
      return;
    }

    setSelectedPhoneNumber(phoneNumber);
    setSelectedRecruiterInfo(recruiterInfo);

    const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);

    if (isMobile) {
      window.location.href = `tel:${phoneNumber}`;
    } else {
      setPhoneDialogOpen(true);
    }
  }, []);

  // Copy to clipboard handler
  const handleCopyToClipboard = async (text) => {
    try {
      await navigator.clipboard.writeText(text);
      const originalText = "Copy to clipboard";
      const button = document.querySelector('[title="Copy to clipboard"]');
      if (button) {
        button.title = "Copied!";
        setTimeout(() => {
          button.title = originalText;
        }, 2000);
      }
    } catch (error) {
      console.error("Failed to copy to clipboard:", error);
      setErrorDialogMessage("Failed to copy phone number to clipboard");
      setErrorDialogOpen(true);
    }
  };

  const handleDeleteClick = useCallback((jobId) => {
    setJobToDelete(jobId);
    setConfirmDialogOpen(true);
  }, []);

  // Debounce search term
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
    }, 500); // 500ms delay

    return () => clearTimeout(timer);
  }, [searchTerm]);

  // Fetch jobs with status filter
  useEffect(() => {
    async function fetchJobs() {
      try {
        const { jobs, pagination } = await getJobs(jobPage, jobLimit, debouncedSearchTerm, sortOption, statusFilter);
        setJobsData(jobs);
        setJobTotal(pagination?.total || 0);
        setTotalPages(Math.ceil((pagination?.total || 0) / jobLimit));
      } catch (error) {
        setErrorDialogMessage(error.message || "Failed to fetch jobs");
        setErrorDialogOpen(true);
      }
    }
    fetchJobs();
  }, [jobPage, jobLimit, debouncedSearchTerm, sortOption, statusFilter]);

  useEffect(() => {
    const formattedJobs = jobsData.map((job) => ({
      title: (
        <MDBox display="flex" alignItems="center" gap={1}>
          <MDAvatar src={job.companyLogo} name={job.companyName || "Company"} size="sm" />
          <MDBox display="flex" flexDirection="column">
            <MDTypography variant="button" fontWeight="medium">
              {job.role || "Unspecified"}
            </MDTypography>
            <MDTypography
              variant="caption"
              color="text"
              sx={{
                whiteSpace: "normal",
                wordBreak: "break-word",
                maxWidth: "250px",
                mt: 0.5,
              }}
            >
              {job.companyName || "Unknown"}
              {job.requireManualVerification === true && job.status === "pending" && (
                <>
                  <br />
                  <span
                    style={{
                      color: "#d32f2f",
                      fontSize: "0.75rem",
                      display: "flex",
                      alignItems: "center",
                      gap: "4px",
                      marginTop: "4px",
                      marginBottom: "2px",
                    }}
                  >
                    <i className="material-icons" style={{ fontSize: "1rem" }}>
                      info
                    </i>{" "}
                    Require manual approval
                  </span>
                </>
              )}
              {job.requireManualVerification === false && job.status === "rejected" && (
                <>
                  <br />
                  <span
                    style={{
                      color: "#d32f2f",
                      fontSize: "0.75rem",
                      display: "flex",
                      alignItems: "center",
                      gap: "4px",
                      marginTop: "4px",
                      marginBottom: "2px",
                    }}
                  >
                    {job.rejectionError || "Unknown reason"}
                  </span>
                </>
              )}
            </MDTypography>
          </MDBox>
        </MDBox>
      ),
      salary: (
        <MDTypography variant="caption" color="text" fontWeight="medium">
          {job.salaryType === "notDisclosed"
            ? "Not Disclosed"
            : job.salary?.fixed
              ? `₹${job.salary.fixed} ${job.salary.period}`
              : job.salary?.min || job.salary?.max
                ? `₹${job.salary.min || 0}-${job.salary.max || 0} ${job.salary.period}`
                : "N/A"}
        </MDTypography>
      ),
      jobType: (
        <MDTypography variant="caption" color="text" fontWeight="medium">
          {formatJobType(job.jobType || "N/A")}
        </MDTypography>
      ),
      applications: (
        <MDTypography variant="caption" color="text" fontWeight="medium">
          {job.totalApplications || 0}
        </MDTypography>
      ),
      createdAt: (
        <MDTypography variant="caption" color="text" fontWeight="medium">
          {job.createdAt
            ? new Date(job.createdAt).toLocaleString(undefined, {
              day: "2-digit",
              month: "2-digit",
              year: "2-digit",
              hour: "2-digit",
              minute: "2-digit",
              hour12: true,
            })
            : ""}
        </MDTypography>
      ),
      status: (
        <select
          value={job.status || "pending"}
          onChange={(e) => handleStatusChange(job._id, e.target.value)}
          style={{
            padding: "4px 8px",
            borderRadius: "4px",
            border: "1px solid #ccc",
            fontSize: "0.875rem",
            backgroundColor:
              {
                pending: "#fff3cd",
                published: "#d4edda",
                rejected: "#f8d7da",
                closed: "#d1d1d1",
                paused: "#e2e3e5",
              }[job.status] || "#fff",
            color:
              {
                pending: "#856404",
                published: "#155724",
                rejected: "#721c24",
                closed: "#383d41",
                paused: "#6c757d",
              }[job.status] || "#000",
          }}
        >
          <option value="pending">Pending</option>
          <option value="published">Published</option>
          <option value="rejected">Rejected</option>
          <option value="closed">Closed</option>
          <option value="paused">Paused</option>
        </select>
      ),
      action: (
        <MDBox display="flex" alignItems="center" gap={1}>
          <IconButton
            size="small"
            color="info"
            onClick={() => handlePhoneClick(job.phone?.number, {
              companyName: job.companyName,
              companyLogo: job.companyLogo,
              jobTitle: job.role,
              recruiterName: job.recruiterName || job.postedBy?.name || job.contactPerson || "Recruiter"
            })}
            disabled={!job.phone?.number}
            title={job.phone?.number ? `Call ${job.phone.number}` : "Phone number not available"}
          >
            <PhoneIcon fontSize="small" />
          </IconButton>
          <IconButton size="small" color="info" onClick={() => handleViewClick(job._id)}>
            <VisibilityIcon fontSize="small" />
          </IconButton>
          <IconButton size="small" color="info" onClick={() => handleEditJob(job)}>
            <EditIcon fontSize="small" />
          </IconButton>
          <IconButton size="small" color="error" onClick={() => handleDeleteClick(job._id)}>
            <DeleteIcon fontSize="small" />
          </IconButton>
        </MDBox>
      ),
      // Add sorting fields for client-side sorting
      jobTitle: job.role || "Unspecified",
      companyName: job.companyName || "Unknown",
      createdAtDate: job.createdAt ? new Date(job.createdAt) : new Date(0),
    }));

    setJobRows(formattedJobs);
  }, [jobsData, sortOption, statusFilter]);

  const confirmDeleteJob = async () => {
    if (!jobToDelete) return;
    setConfirmDialogOpen(false);
    setIsDeleting(true);
    try {
      await deleteJob(jobToDelete);
      setJobsData((prevJobs) => prevJobs.filter((job) => job._id !== jobToDelete));
    } catch (error) {
      setErrorDialogMessage(error.message || "Failed to delete job");
      setErrorDialogOpen(true);
    } finally {
      setIsDeleting(false);
      setJobToDelete(null);
    }
  };

  const handlePageChange = (_, value) => {
    setJobPage(value);
  };

  // Helper functions for job editing
  const getEditableJobFields = (job) => {
    const fields = [
      // Basic job information
      { label: "Job Role", key: "role", type: "text", required: true },
      { label: "Industry Type", key: "industryType", type: "text", required: true },
      { label: "Job Description", key: "description", type: "textarea", required: true },

      // Location fields
      { label: "Location Address", key: "locationAddress", type: "text", required: true },
      { label: "Location City", key: "locationCity", type: "text", required: false },

      // Job type and details
      {
        label: "Job Type",
        key: "jobType",
        type: "select",
        options: [
          { value: "remote", label: "Remote" },
          { value: "fullTime", label: "Full Time" },
          { value: "partTime", label: "Part Time" },
          { value: "contract", label: "Contract" },
          { value: "internship", label: "Internship" },
        ],
        required: true,
      },
      {
        label: "Interview Type",
        key: "interviewType",
        type: "select",
        options: [
          { value: "inPerson", label: "In Person" },
          { value: "virtual", label: "Virtual" },
        ],
        required: false,
      },
      {
        label: "Job Shift",
        key: "shift",
        type: "select",
        options: [
          { value: "day", label: "Day Shift" },
          { value: "night", label: "Night Shift" },
          { value: "any", label: "Any" },
        ],
        required: false,
      },
      {
        label: "Experience Level",
        key: "experienceLevel",
        type: "select",
        options: [
          { value: "fresher", label: "Fresher" },
          { value: "sixToOneMonth", label: "6 Months to 1 Year" },
          { value: "oneToTwoYear", label: "1 to 2 Years" },
          { value: "twoToThreeYear", label: "2 to 3 Years" },
          { value: "threeToFiveYear", label: "3 to 5 Years" },
          { value: "fivePlusYear", label: "5+ Years" },
          { value: "any", label: "Any Experience" },
        ],
        required: true,
      },

      // Skills and requirements
      {
        label: "Skills Required (comma-separated)",
        key: "skills",
        type: "textarea",
        required: true,
      },
      {
        label: "Require Immediate Joiner",
        key: "requireImmediateJoiner",
        type: "checkbox",
        required: false,
      },
      { label: "Number of Vacancies", key: "vacancies", type: "number", required: false },

      // Contact information
      { label: "Contact Phone Number", key: "phoneNumber", type: "text", required: true },
      { label: "Make Phone Public", key: "phoneIsPublic", type: "checkbox", required: false },
      {
        label: "Contact Start Time",
        key: "contactStartTime",
        type: "time",
        required: true,
      },
      {
        label: "Contact End Time",
        key: "contactEndTime",
        type: "time",
        required: true,
      },
      {
        label: "Available Contact Days",
        key: "availableContactDays",
        type: "select",
        options: [
          { value: "mon", label: "Monday" },
          { value: "tue", label: "Tuesday" },
          { value: "wed", label: "Wednesday" },
          { value: "thu", label: "Thursday" },
          { value: "fri", label: "Friday" },
          { value: "sat", label: "Saturday" },
          { value: "sun", label: "Sunday" },
        ],
        multiple: true,
        required: false,
      },
      { label: "Contact Email", key: "email", type: "email", required: false },

      // Resume requirements
      {
        label: "Resume Requirement",
        key: "resumeRequirement",
        type: "select",
        options: [
          { value: "required", label: "Required" },
          { value: "optional", label: "Optional" },
          { value: "notNeeded", label: "Not Needed" },
        ],
        required: true,
      },
    ];

    // Add salary fields based on salary type
    const salaryType = job.salary?.salaryType || "notDisclosed";
    const salaryFields = [];

    // Always add salary type first
    salaryFields.push({
      label: "Salary Type",
      key: "salaryType",
      type: "select",
      options: [
        { value: "fixed", label: "Fixed Salary" },
        { value: "range", label: "Salary Range" },
        { value: "notDisclosed", label: "Not Disclosed" },
      ],
      required: false,
    });

    // Add specific salary fields based on type
    if (salaryType === "fixed") {
      salaryFields.push({
        label: "Fixed Salary",
        key: "salaryFixed",
        type: "number",
        required: false,
      });
    } else if (salaryType === "range") {
      salaryFields.push(
        { label: "Min Salary", key: "salaryMin", type: "number", required: false },
        { label: "Max Salary", key: "salaryMax", type: "number", required: false }
      );
    }

    // Add currency and period only if salary is not notDisclosed
    if (salaryType !== "notDisclosed") {
      salaryFields.push(
        {
          label: "Currency",
          key: "salaryCurrency",
          type: "select",
          options: [
            { value: "INR", label: "INR (₹)" },
          ],
          required: false,
        },
        {
          label: "Salary Period",
          key: "salaryPeriod",
          type: "select",
          options: [
            { value: "monthly", label: "Monthly" },
            { value: "yearly", label: "Yearly" },
            { value: "weekly", label: "Weekly" },
            { value: "hourly", label: "Hourly" },
          ],
          required: false,
        }
      );
    }

    // Insert salary fields after job description (index 2)
    fields.splice(3, 0, ...salaryFields);

    return fields;
  };

  const getJobFormData = (job) => {
    const formatTime = (hour, minute) => {
      if (hour === undefined || minute === undefined) return "";
      return `${hour.toString().padStart(2, "0")}:${minute.toString().padStart(2, "0")}`;
    };

    return {
      role: job.role || "",
      industryType: job.industryType || "",
      description: job.description || "",
      locationAddress: job.location?.address || "",
      locationCity: job.location?.city || "",
      jobType: job.jobType || "fullTime",
      interviewType: job.interviewType || "",
      shift: job.shift || "",
      experienceLevel: job.experienceLevel || "",
      salaryType: job.salary?.salaryType || "notDisclosed",
      salaryCurrency: job.salary?.currency || "INR",
      salaryFixed: job.salary?.fixed || "",
      salaryMin: job.salary?.min || "",
      salaryMax: job.salary?.max || "",
      salaryPeriod: job.salary?.period || "",
      skills: Array.isArray(job.skills) ? job.skills.join(", ") : job.skills || "",
      requireImmediateJoiner: job.requireImmediateJoiner || false,
      vacancies: job.vacancies || "",
      phoneNumber: job.phone?.number || "",
      phoneIsPublic: job.phone?.isPublic !== undefined ? job.phone.isPublic : true,
      contactStartTime: formatTime(
        job.phone?.contactStartTime?.hour,
        job.phone?.contactStartTime?.minute
      ),
      contactEndTime: formatTime(
        job.phone?.contactEndTime?.hour,
        job.phone?.contactEndTime?.minute
      ),
      availableContactDays: job.phone?.availableContactDays || [],
      email: job.email || "",
      resumeRequirement: job.resumeRequirement || "required",
    };
  };

  const updateJobDataFromForm = (job, formData) => {
    const updated = { ...job };

    const parseTime = (timeString) => {
      if (!timeString) return { hour: 0, minute: 0 };
      const [hour, minute] = timeString.split(":").map(Number);
      return { hour: hour || 0, minute: minute || 0 };
    };

    updated.role = formData.role;
    updated.industryType = formData.industryType;
    updated.description = formData.description;
    updated.jobType = formData.jobType;
    updated.interviewType = formData.interviewType;
    updated.shift = formData.shift;
    updated.experienceLevel = formData.experienceLevel;

    updated.location = {
      ...updated.location,
      address: formData.locationAddress || "",
      city: formData.locationCity || "",
    };

    updated.salary = {
      ...updated.salary,
      salaryType: formData.salaryType,
      currency: formData.salaryCurrency,
      period: formData.salaryPeriod,
    };

    if (formData.salaryType === "fixed") {
      updated.salary.fixed = formData.salaryFixed ? Number(formData.salaryFixed) : null;
      delete updated.salary.min;
      delete updated.salary.max;
    } else if (formData.salaryType === "range") {
      updated.salary.min = formData.salaryMin ? Number(formData.salaryMin) : null;
      updated.salary.max = formData.salaryMax ? Number(formData.salaryMax) : null;
      delete updated.salary.fixed;
    } else {
      delete updated.salary.fixed;
      delete updated.salary.min;
      delete updated.salary.max;
    }

    updated.skills = formData.skills ? formData.skills.split(",").map((skill) => skill.trim()) : [];
    updated.requireImmediateJoiner = formData.requireImmediateJoiner;
    updated.vacancies = formData.vacancies ? Number(formData.vacancies) : null;

    updated.phone = {
      ...updated.phone,
      number: formData.phoneNumber,
      isPublic: formData.phoneIsPublic,
      contactStartTime: parseTime(formData.contactStartTime),
      contactEndTime: parseTime(formData.contactEndTime),
      availableContactDays: formData.availableContactDays || [],
    };

    updated.email = formData.email;
    updated.resumeRequirement = formData.resumeRequirement;

    return updated;
  };

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox pt={6} pb={3}>
        <Grid container spacing={6}>
          <Grid item xs={12}>
            <Card>
              <MDBox
                mx={2}
                mt={-3}
                py={3}
                px={2}
                variant="gradient"
                bgColor="info"
                borderRadius="lg"
                coloredShadow="info"
              >
                <MDTypography variant="h6" color="white">
                  Jobs Table
                </MDTypography>
              </MDBox>
              <MDBox px={2} pt={2}>
                <MDBox
                  display="flex"
                  alignItems="center"
                  gap={2}
                  mb={2}
                  sx={{
                    flexDirection: { xs: "column", md: "row" },
                    alignItems: { xs: "stretch", md: "center" },
                    "& > *": {
                      width: { xs: "100%", md: "auto" }
                    }
                  }}
                >
                  <MDBox
                    display="flex"
                    alignItems="center"
                    sx={{
                      flex: { xs: "none", md: 1 },
                      width: { xs: "100%", md: "auto" },
                      minWidth: 0
                    }}
                  >
                    <input
                      type="text"
                      placeholder="Search by job title, company, or location"
                      value={searchTerm}
                      onChange={handleSearchChange}
                      onKeyDown={handleKeyDown}
                      style={{
                        flex: 1,
                        padding: "10px",
                        fontSize: "14px",
                        borderRadius: "6px",
                        border: "1px solid #ccc",
                        minWidth: 0,
                      }}
                    />
                    {searchTerm && (
                      <IconButton onClick={handleClearSearch} size="small">
                        <ClearIcon fontSize="small" />
                      </IconButton>
                    )}
                  </MDBox>
                  <MDBox display="flex" alignItems="center" gap={1} sx={{ minWidth: 0 }}>
                    <MDTypography
                      variant="caption"
                      fontWeight="medium"
                      sx={{
                        whiteSpace: "nowrap",
                        display: { xs: "none", sm: "block" }
                      }}
                    >
                      Filter:
                    </MDTypography>
                    <FormControl size="small" sx={{ flex: 1, minWidth: "120px" }}>
                      <Select
                        value={statusFilter}
                        onChange={handleStatusFilterChange}
                        displayEmpty
                        sx={{
                          height: "36px",
                          fontSize: "14px",
                          "& .MuiSelect-select": {
                            padding: "8px 24px 8px 12px",
                          },
                        }}
                      >
                        <MenuItem value="all">All Jobs</MenuItem>
                        <MenuItem value="published">Published</MenuItem>
                        <MenuItem value="pending">Pending</MenuItem>
                        <MenuItem value="closed">Closed</MenuItem>
                        <MenuItem value="rejected">Rejected</MenuItem>
                        <MenuItem value="paused">Paused</MenuItem>
                      </Select>
                    </FormControl>
                  </MDBox>

                  <MDBox display="flex" alignItems="center" gap={1} sx={{ minWidth: 0 }}>
                    <MDTypography
                      variant="caption"
                      fontWeight="medium"
                      sx={{
                        whiteSpace: "nowrap",
                        display: { xs: "none", sm: "block" }
                      }}
                    >
                      Sort by:
                    </MDTypography>
                    <Select
                      value={sortOption}
                      onChange={handleSortChange}
                      sx={{
                        height: "36px",
                        fontSize: "14px",
                        flex: 1,
                        minWidth: "140px",
                        "& .MuiSelect-select": {
                          padding: "8px 24px 8px 12px",
                        },
                      }}
                    >
                      {sortOptions.map((option) => (
                        <MenuItem key={option.value} value={option.value}>
                          {option.label}
                        </MenuItem>
                      ))}
                    </Select>
                  </MDBox>
                </MDBox>
              </MDBox>
              <MDBox pt={3}>
                <DataTable
                  table={{ columns: jobColumns, rows: jobRows }}
                  isSorted={false}
                  entriesPerPage={false}
                  showTotalEntries={false}
                  noEndBorder
                />
              </MDBox>
              <MDBox
                display="flex"
                alignItems="center"
                justifyContent="center"
                mb={2}
                mt={2}
                px={3}
              >
                <Pagination
                  count={totalPages}
                  page={jobPage}
                  onChange={handlePageChange}
                  color="primary"
                  showFirstButton={window.innerWidth > 768}
                  showLastButton={window.innerWidth > 768}
                  siblingCount={window.innerWidth > 768 ? 1 : 0}
                  boundaryCount={window.innerWidth > 768 ? 1 : 1}
                  sx={{
                    ml: { xs: 0, sm: 2 },
                    "& .MuiPaginationItem-root": {
                      fontSize: { xs: "0.75rem", sm: "0.875rem" },
                      minWidth: { xs: "28px", sm: "32px" },
                      height: { xs: "28px", sm: "32px" },
                    }
                  }}
                />
              </MDBox>
            </Card>
          </Grid>
        </Grid>
      </MDBox>
      <Footer />
      <ConfirmDialog
        open={confirmDialogOpen}
        onClose={() => setConfirmDialogOpen(false)}
        onConfirm={confirmDeleteJob}
        description="Are you sure you want to delete this job? This action cannot be undone."
        confirmButtonText="Delete"
        confirmButtonColor="error"
      />
      <Dialog
        open={errorDialogOpen}
        onClose={() => setErrorDialogOpen(false)}
        aria-labelledby="error-dialog-title"
        aria-describedby="error-dialog-description"
      >
        <DialogTitle id="error-dialog-title">{"Error updating job status"}</DialogTitle>
        <DialogContent dividers>
          <MDTypography>{errorDialogMessage}</MDTypography>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setErrorDialogOpen(false)}
            color="primary"
            variant="contained"
            autoFocus
            sx={{
              height: "40px",
              backgroundColor: "#1976d2",
              color: "#ffffff",
              "&:hover": {
                backgroundColor: "#1565c0",
              },
            }}
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>
      {editDialogOpen && selectedJob && (
        <EditDialog
          open={editDialogOpen}
          onClose={() => setEditDialogOpen(false)}
          title="Edit Job"
          fields={getEditableJobFields(selectedJob)}
          formData={getJobFormData(selectedJob)}
          setFormData={(data) => {
            const updatedJob = updateJobDataFromForm(selectedJob, data);
            setSelectedJob(updatedJob);
          }}
          onSubmit={confirmEditJob}
        />
      )}
      <Dialog
        open={rejectionDialogOpen}
        onClose={() => {
          setRejectionDialogOpen(false);
          setRejectionReason("");
          setJobToReject(null);
        }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Reject Job</DialogTitle>
        <DialogContent dividers>
          <MDTypography variant="body2" color="text" mb={2}>
            Please provide a reason for rejecting this job:
          </MDTypography>
          <TextField
            autoFocus
            margin="dense"
            label="Rejection Reason"
            type="text"
            fullWidth
            multiline
            rows={3}
            variant="outlined"
            value={rejectionReason}
            onChange={(e) => setRejectionReason(e.target.value)}
            placeholder="Enter reason for rejection..."
          />
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setRejectionDialogOpen(false);
              setRejectionReason("");
              setJobToReject(null);
            }}
            color="secondary"
            sx={{
              height: "40px",
              color: "#757575",
              "&:hover": {
                backgroundColor: "#f5f5f5",
              },
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleRejectJob}
            color="error"
            variant="contained"
            disabled={!rejectionReason.trim()}
            sx={{
              height: "40px",
              backgroundColor: "#d32f2f",
              color: "#ffffff",
              "&:hover": {
                backgroundColor: "#b71c1c",
              },
              "&:disabled": {
                backgroundColor: "#e0e0e0",
                color: "#9e9e9e",
              },
            }}
          >
            Reject Job
          </Button>
        </DialogActions>
      </Dialog>

      {/* View Applicants Dialog */}
      <Dialog
        open={viewDialogOpen}
        onClose={() => setViewDialogOpen(false)}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            margin: { xs: 1, sm: 2 },
            maxHeight: { xs: "90vh", sm: "80vh" },
            borderRadius: { xs: "12px", sm: "20px" }
          }
        }}
      >
        <DialogTitle sx={{ pb: 2, pt: 3, px: { xs: 2, sm: 3 } }}>
          <MDTypography variant="h5" fontWeight="medium">
            Job Applicants
          </MDTypography>
        </DialogTitle>
        <DialogContent dividers sx={{ px: { xs: 1, sm: 3 }, py: { xs: 2, sm: 3 } }}>
          <MDBox>
            {viewApplicants.length === 0 ? (
              <MDTypography variant="body2" color="text" textAlign="center" py={3}>
                No applicants found for this job.
              </MDTypography>
            ) : (
              viewApplicants.map((applicant) => (
                <MDBox
                  key={applicant._id}
                  display="flex"
                  gap={2}
                  p={{ xs: 1.5, sm: 2 }}
                  mb={2}
                  sx={{
                    border: "1px solid #e0e0e0",
                    borderRadius: "8px",
                    backgroundColor: "#fafafa",
                    flexDirection: { xs: "column", sm: "row" },
                    alignItems: { xs: "flex-start", sm: "center" }
                  }}
                >
                  <MDBox
                    display="flex"
                    alignItems="center"
                    gap={2}
                    sx={{
                      width: { xs: "100%", sm: "auto" },
                      justifyContent: { xs: "space-between", sm: "flex-start" }
                    }}
                  >
                    <MDBox display="flex" alignItems="center" gap={2}>
                      <MDAvatar
                        src={applicant.profilePicture}
                        name={applicant.name}
                        size="md"
                      />
                      <MDBox>
                        <MDTypography variant="h6" fontWeight="medium" sx={{ fontSize: { xs: "1rem", sm: "1.125rem" } }}>
                          {applicant.name}
                        </MDTypography>
                        <MDBox display="flex" alignItems="center" gap={1}>
                          <LocationOnIcon sx={{ fontSize: 14, color: '#9e9e9e' }} />
                          <MDTypography variant="caption" color="text">
                            {applicant.location}
                          </MDTypography>
                        </MDBox>
                      </MDBox>
                    </MDBox>
                    <MDBox sx={{ display: { xs: "block", sm: "none" } }}>
                      <MDTypography
                        variant="caption"
                        color={
                          applicant.status === "pending"
                            ? "warning"
                            : applicant.status === "reviewed"
                              ? "info"
                              : applicant.status === "shortlisted"
                                ? "success"
                                : "error"
                        }
                        sx={{
                          backgroundColor:
                            applicant.status === "pending"
                              ? "#fff3cd"
                              : applicant.status === "reviewed"
                                ? "#d1ecf1"
                                : applicant.status === "shortlisted"
                                  ? "#d4edda"
                                  : "#f8d7da",
                          padding: "4px 8px",
                          borderRadius: "4px",
                          textTransform: "capitalize",
                          fontSize: "0.75rem",
                          fontWeight: "medium"
                        }}
                      >
                        {applicant.status}
                      </MDTypography>
                    </MDBox>
                  </MDBox>

                  <MDBox flex={1} sx={{ width: { xs: "100%", sm: "auto" } }}>
                    <MDBox
                      display="flex"
                      gap={{ xs: 1, sm: 2 }}
                      mt={{ xs: 1, sm: 0 }}
                      sx={{
                        flexDirection: { xs: "column", sm: "row" },
                        flexWrap: "wrap"
                      }}
                    >
                      <MDBox display="flex" alignItems="center" gap={0.5} sx={{ minWidth: 0 }}>
                        <PhoneIcon sx={{ fontSize: 14, color: '#9e9e9e' }} />
                        <MDTypography
                          variant="caption"
                          color="text"
                          sx={{
                            fontSize: { xs: "0.7rem", sm: "0.75rem" },
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap"
                          }}
                        >
                          {applicant.phone}
                        </MDTypography>
                      </MDBox>
                      <MDBox display="flex" alignItems="center" gap={0.5} sx={{ minWidth: 0 }}>
                        <EmailIcon sx={{ fontSize: 14, color: '#9e9e9e' }} />
                        <MDTypography
                          variant="caption"
                          color="text"
                          sx={{
                            fontSize: { xs: "0.7rem", sm: "0.75rem" },
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap"
                          }}
                        >
                          {applicant.email}
                        </MDTypography>
                      </MDBox>
                    </MDBox>

                    <MDBox
                      display="flex"
                      gap={{ xs: 1, sm: 2 }}
                      mt={1}
                      sx={{
                        flexDirection: { xs: "column", sm: "row" },
                        flexWrap: "wrap"
                      }}
                    >
                      <MDBox display="flex" alignItems="center" gap={0.5}>
                        <WorkIcon sx={{ fontSize: 14, color: '#9e9e9e' }} />
                        <MDTypography variant="caption" color="text" sx={{ fontSize: { xs: "0.7rem", sm: "0.75rem" } }}>
                          {applicant.experience} experience
                        </MDTypography>
                      </MDBox>
                      <MDBox display="flex" alignItems="center" gap={0.5}>
                        <CalendarTodayIcon sx={{ fontSize: 14, color: '#9e9e9e' }} />
                        <MDTypography variant="caption" color="text" sx={{ fontSize: { xs: "0.7rem", sm: "0.75rem" } }}>
                          Applied: {new Date(applicant.appliedAt).toLocaleDateString()}
                        </MDTypography>
                      </MDBox>
                    </MDBox>

                    {applicant.skills && applicant.skills.length > 0 && (
                      <MDBox display="flex" gap={1} mt={1} flexWrap="wrap">
                        {applicant.skills.slice(0, 5).map((skill, index) => (
                          <MDBox
                            key={index}
                            px={1}
                            py={0.5}
                            sx={{
                              backgroundColor: "#e3f2fd",
                              borderRadius: "4px",
                              fontSize: { xs: "0.65rem", sm: "0.75rem" },
                            }}
                          >
                            {skill}
                          </MDBox>
                        ))}
                        {applicant.skills.length > 5 && (
                          <MDBox
                            px={1}
                            py={0.5}
                            sx={{
                              backgroundColor: "#f5f5f5",
                              borderRadius: "4px",
                              fontSize: { xs: "0.65rem", sm: "0.75rem" },
                              color: "#666"
                            }}
                          >
                            +{applicant.skills.length - 5} more
                          </MDBox>
                        )}
                      </MDBox>
                    )}
                  </MDBox>

                  <MDBox sx={{ display: { xs: "none", sm: "block" } }}>
                    <MDTypography
                      variant="caption"
                      color={
                        applicant.status === "pending"
                          ? "warning"
                          : applicant.status === "reviewed"
                            ? "info"
                            : applicant.status === "shortlisted"
                              ? "success"
                              : "error"
                      }
                      sx={{
                        backgroundColor:
                          applicant.status === "pending"
                            ? "#fff3cd"
                            : applicant.status === "reviewed"
                              ? "#d1ecf1"
                              : applicant.status === "shortlisted"
                                ? "#d4edda"
                                : "#f8d7da",
                        padding: "4px 8px",
                        borderRadius: "4px",
                        textTransform: "capitalize",
                      }}
                    >
                      {applicant.status}
                    </MDTypography>
                  </MDBox>
                </MDBox>
              ))
            )}
          </MDBox>
        </DialogContent>
        <DialogActions sx={{ px: { xs: 2, sm: 3 }, py: { xs: 1.5, sm: 2 } }}>
          <Button
            onClick={() => setViewDialogOpen(false)}
            color="primary"
            autoFocus
            sx={{
              fontSize: { xs: "0.875rem", sm: "1rem" },
              padding: { xs: "8px 16px", sm: "10px 20px" }
            }}
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>

      {/* Phone Call Dialog */}
      <Dialog
        open={phoneDialogOpen}
        onClose={() => setPhoneDialogOpen(false)}
        maxWidth="xs"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: "20px",
            boxShadow: "0 10px 40px rgba(0,0,0,0.15)",
            overflow: "hidden"
          }
        }}
      >
        <DialogTitle sx={{ pb: 2, pt: 3, px: 3, position: "relative" }}>
          <MDBox display="flex" alignItems="center" gap={2}>
            <MDBox
              display="flex"
              alignItems="center"
              justifyContent="center"
              sx={{
                width: 50,
                height: 50,
                borderRadius: "12px",
                backgroundColor: "#1976d2"
              }}
            >
              <PhoneIcon sx={{ color: "#ffffff", fontSize: 24 }} />
            </MDBox>
            <MDBox flex={1}>
              <MDTypography variant="h6" fontWeight="medium" color="dark">
                {selectedRecruiterInfo?.jobTitle || "Job Position"}
              </MDTypography>
              <MDTypography variant="caption" color="text" sx={{ fontSize: "0.85rem" }}>
                {selectedRecruiterInfo?.companyName || "Company Name"}
              </MDTypography>
            </MDBox>
          </MDBox>
          <IconButton
            onClick={() => setPhoneDialogOpen(false)}
            sx={{
              position: "absolute",
              top: 16,
              right: 16,
              color: "#666",
              "&:hover": {
                backgroundColor: "#f5f5f5"
              }
            }}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </DialogTitle>

        <DialogContent sx={{ pt: 0, pb: 3, px: 3 }}>
          <MDBox
            display="flex"
            alignItems="center"
            justifyContent="space-between"
            p={2}
            sx={{
              backgroundColor: "#f8f9fa",
              borderRadius: "12px",
              border: "1px solid #e9ecef"
            }}
          >
            <MDBox display="flex" alignItems="center" gap={1.5}>
              <PhoneIcon sx={{ color: "#1976d2", fontSize: 20 }} />
              <MDTypography variant="body1" fontWeight="medium" color="dark" sx={{ fontSize: "1rem" }}>
                {selectedPhoneNumber}
              </MDTypography>
            </MDBox>
            <IconButton
              size="small"
              onClick={() => handleCopyToClipboard(selectedPhoneNumber)}
              sx={{
                color: "#1976d2",
                width: 32,
                height: 32,
                "&:hover": {
                  backgroundColor: "#f0f7ff"
                }
              }}
              title="Copy to clipboard"
            >
              <ContentCopyIcon sx={{ fontSize: 20 }} />
            </IconButton>
          </MDBox>
        </DialogContent>
      </Dialog>
    </DashboardLayout>
  );
}

export default Jobs;